import { z } from 'zod';

const IconSchema = z.object({
  icon1: z.string(),
  alt_text: z.string().optional()
});

const FirstYearSchema = z.object({
  year: z.string(),
  price: z.string(),
  gst: z.string(),
  gst_price: z.string(),
  price_without_gst: z.string(),
  normal_price: z.string(),
  show_one_year: z.boolean()
});

const SecondYearSchema = z.object({
  year: z.string(),
  second_year_price: z.string()
});

const AvailablePlansSchema = z.object({
  link: z.string(),
  text: z.string()
});

const TrainingFeeSchema = z.object({
  price: z.string(),
  gst: z.string(),
  total_price: z.string()
});

const AdminstrationFeeSchema = z.object({
  price: z.string(),
  gst: z.string(),
  total_price: z.string()
});

const XPMFeeSchema = z.object({
  price: z.string(),
  gst: z.string(),
  total_price: z.string()
});

const SectionDetailsSchema = z.object({
  title: z.string(),
  image: z.string(),
  points: z.array(z.string()).optional(),
  note: z.array(z.string()).optional()
});

const SuccessPaymentSchema = z.object({
  title: z.string(),
  description: z.string(),
  url: z.string(),
  success_button_text: z.string().optional(),
  show_success_button: z.boolean().optional(),
  condition_based_redirection_urls: z
    .array(
      z.object({
        redirection_url: z.string(),
        redirection_condition: z.string()
      })
    )
    .optional()
});

const WhatsappButtonConfigSchema = z.object({
  text: z.string().optional(),
  phone: z.string().optional()
});

const FailurePaymentSchema = z.object({
  title: z.string(),
  description: z.string(),
  url: z.string().optional(),
  failure_button_text: z.string(),
  button_type: z.string().optional(),
  whatsapp_button_config: WhatsappButtonConfigSchema.optional()
});

const FeeBreakupInfoSchema = z.object({
  name: z.string(),
  price: z.string(),
  gst: z.string(),
  total: z.string()
});

const CardDetailsSchema = z.object({
  header: z.string(),
  show_cardless_emi: z.boolean().optional(),
  first_year: FirstYearSchema.optional(),
  second_year: SecondYearSchema.optional(),
  down_payment_price: z.string().optional(),
  emi_for_9_months_price: z.string().optional(),
  emi_for_6_months_price: z.string().optional(),
  button_text: z.string(),
  button_link: z.string(),
  available_plans: AvailablePlansSchema.optional(),
  training_fee: TrainingFeeSchema.optional(),
  administration_fee: AdminstrationFeeSchema.optional(),
  xpm_fee: XPMFeeSchema.optional(),
  ccbp_details: z.array(z.string()).optional(),
  card_less_emi_details: z.any(), //TODO: change to proper data type
  section_details: z.array(SectionDetailsSchema).optional(),
  success_payment: SuccessPaymentSchema,
  failure_payment: FailurePaymentSchema,
  fee_breakup: z.string().optional(),
  fee_breakup_info: z.array(FeeBreakupInfoSchema).optional(),
  know_more_link: z.string().optional()
});

const ErrorMessageWithActionTypesSchema = z.object({
  action: z.string()
});

const ErrorMessageObjectSchema = z.object({
  alreadyPaidForPartialProduct: z.string().optional(),
  purchaseEligibilityDidNotMatch: z.string().optional(),
  alreadyRegistered: z
    .union([z.string(), ErrorMessageWithActionTypesSchema])
    .optional(),
  productExpired: z.string().optional()
});

const InstallmentsDetailsObjectSchema = z.object({
  pathId: z.string(),
  installmentId: z.string(),
  noOfInstallments: z.string()
});

const CollegesWebinarSpeakerDetailsSchema = z.object({
  speaker_name: z.string(),
  speaker_designation: z.string(),
  speaker_description: z.string(),
  speaker_image_url: z.string()
});

const CollegesWebinarRegistrationFormDetailsSchema = z.object({
  webinar_title: z.string(),
  title_description: z.string(),
  webinar_heading: z.string(),
  webinar_secondary_heading: z.string(),
  heading_description: z.string(),
  speaker_details: CollegesWebinarSpeakerDetailsSchema.optional()
});

const RegistrationFormSpeakersBgImageDetailsSchema = z.object({
  desktop: z.string().optional(),
  mobile: z.string().optional()
});

const RegistrationFormSpeakerImageDetailsSchema = z.object({
  desktop: z.record(z.string(), z.string()),
  mobile: z.record(z.string(), z.string())
});

const RegistrationFormWebinarDateDetailsSchema = z.object({
  webinar_dates: z.string()
});

const RegistrationFormDetailsSchema = z.object({
  form_info_heading: z.string().optional(),
  form_info_secondary_mobile_heading: z.string().optional(),
  form_info_description: z.string().optional(),
  speakers_image_urls: RegistrationFormSpeakerImageDetailsSchema.optional(),
  speakers_bg_image_urls:
    RegistrationFormSpeakersBgImageDetailsSchema.optional(),
  should_show_mobile_heading: z.boolean().optional(),
  webinar_date_details: RegistrationFormWebinarDateDetailsSchema.optional()
});

export const ProductExtraDataSchema = z.object({
  icons: IconSchema,
  order: z.number(),
  product_name: z.string(),
  form_type: z.string().optional(),
  page_description: z.string().optional(),
  card_details: CardDetailsSchema.optional(),
  card_header: z.string().optional(),
  card_profile_header_price: z.string().optional(),
  should_show_refund_policy: z.boolean().optional(),
  refund_policy_content: z.string().optional(),
  for_more_details: z.string().optional(),
  payment_details_heading: z.string().optional(),
  payment_details_heading_description: z.string().optional(),
  payment_error_messages: ErrorMessageObjectSchema.optional(),
  should_show_program_info: z.boolean().optional(),
  should_show_payment_terms_and_conditions: z.boolean().optional(),
  should_show_coupon_apply_button: z.boolean().optional(),
  eligible_for_program_change: z.boolean().optional(),
  payment_details_submit_button_text: z.string().optional(),
  submit_button_id: z.string().optional(),
  should_show_omega_fields: z.boolean().optional(),
  periodic_installments_details: z
    .array(InstallmentsDetailsObjectSchema)
    .optional(),
  cta_text: z.string().optional(),
  registration_form_variant: z.string().optional(),
  registration_form_details: RegistrationFormDetailsSchema.optional(),
  colleges_webinar_registration_form_details:
    CollegesWebinarRegistrationFormDetailsSchema.optional(),
  should_format_request_object: z.boolean().optional()
});

const SuccessPaymentMessageBasedOnFieldSchema = z.object({
  title: z.string(),
  description: z.string(),
  url: z.string(),
  success_button_text: z.string().optional(),
  show_success_button: z.boolean().optional(),
  condition: z.string()
});

export const AslWebinarRedirectionUrlsSchema = z.object({
  Telugu: z.string().optional(),
  Hindi: z.string().optional(),
  Tamil: z.string().optional(),
  Kannada: z.string().optional(),
  Marathi: z.string().optional()
});

export const AslWebinarRedirectionConfigSchema = z.object({
  match_key: z.string().optional(),
  redirection_config: AslWebinarRedirectionUrlsSchema
});

export const SuccessPaymentWithWebinarRedirectionSchema = z.object({
  title: z.string(),
  description: z.string(),
  url: z.string(),
  success_button_text: z.string().optional(),
  show_success_button: z.boolean().optional(),
  webinar_variant: z.string().optional(),
  redirectionConfig: AslWebinarRedirectionConfigSchema
});

export const RegistrationMessagesConfigSchema = z.object({
  success_payment: z.union([
    SuccessPaymentSchema,
    SuccessPaymentWithWebinarRedirectionSchema
  ]),
  failure_payment: FailurePaymentSchema,
  button_type: z.string().optional(),
  whatsapp_button_config: WhatsappButtonConfigSchema.optional(),
  success_payment_messages_based_on_field: z
    .array(SuccessPaymentMessageBasedOnFieldSchema)
    .optional()
});

export const ProductErrorMessageConfigSchema = z.object({
  payment_error_messages: ErrorMessageObjectSchema.optional()
});
